import React, { useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import API from '@/utils/api';

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  if (!token) return (
    <div className="min-h-screen bg-[#080808] flex items-center justify-center px-4">
      <div className="text-center">
        <p className="text-rm-red mb-4">Invalid reset link.</p>
        <Link to="/auth" className="btn-primary px-6 py-2.5 text-sm rounded-sm">Back to Login</Link>
      </div>
    </div>
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) return toast.error('Password must be at least 6 characters');
    if (password !== confirm) return toast.error('Passwords do not match');
    setLoading(true);
    try {
      await API.post('/auth/reset-password', { token, new_password: password });
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password. Link may have expired.');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#080808] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md animate-fade-in">
        <div className="bg-[#0d0d0d] border border-[#1e1e1e] rounded-sm p-8">
          {done ? (
            <div className="text-center space-y-4">
              <CheckCircle size={36} className="mx-auto text-green-500" />
              <h2 className="font-rajdhani font-bold text-2xl uppercase">Password Reset!</h2>
              <p className="text-sm text-zinc-500">Your password has been updated successfully. You can now log in.</p>
              <button onClick={() => navigate('/auth')} className="btn-primary px-8 py-3 text-sm rounded-sm mt-2">
                Go to Login
              </button>
            </div>
          ) : (
            <>
              <div className="mb-6">
                <h2 className="font-rajdhani font-bold text-2xl uppercase tracking-wide mb-1">New Password</h2>
                <p className="text-xs text-zinc-600">Enter and confirm your new password below.</p>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-password-form">
                <div className="relative">
                  <label className="block text-[10px] uppercase tracking-[0.15em] text-zinc-600 mb-2">New Password</label>
                  <input type={showPass ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
                    placeholder="Min 6 characters" data-testid="new-password"
                    className="w-full bg-[#0a0a0a] border border-[#222] text-white placeholder:text-zinc-700 px-4 py-3 rounded-sm text-sm pr-10" />
                  <button type="button" onClick={() => setShowPass(!showPass)}
                    className="absolute right-3 bottom-3 text-zinc-600 hover:text-white">
                    {showPass ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.15em] text-zinc-600 mb-2">Confirm Password</label>
                  <input type="password" value={confirm} onChange={e => setConfirm(e.target.value)}
                    placeholder="Repeat new password" data-testid="confirm-password"
                    className="w-full bg-[#0a0a0a] border border-[#222] text-white placeholder:text-zinc-700 px-4 py-3 rounded-sm text-sm" />
                </div>
                <button type="submit" disabled={loading} data-testid="reset-submit"
                  className="w-full btn-primary py-3 rounded-sm text-sm disabled:opacity-50">
                  {loading ? 'Updating...' : 'Reset Password'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
